import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import ISO6391 from "iso-639-1";
import AsyncSelect from "react-select/async";
import "./EditProductLeft.css";
import { Loader } from "lucide-react";
import axiosInstance from "../../utils/api";

const EditProductLeft = ({
  keywordsConfig,
  textareaConfig,
  languageConfig,
  locationConfig,
  onOptimize,
  onTranslate,
  onAddDocuments,
  loading,
}) => {
  const [localKeywords, setLocalKeywords] = useState(
    keywordsConfig?.value || []
  );
  const [inputValue, setInputValue] = useState("");
  const [allLocations, setAllLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const languageOptions = React.useMemo(
    () =>
      ISO6391.getAllNames().map((name) => ({
        value: ISO6391.getCode(name),
        label: name,
      })),
    []
  );

  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  const handleKeyDown = (e) => {
    if (["Enter", ",", "Tab"].includes(e.key)) {
      e.preventDefault();
      const values = inputValue.trim().split(/[\n\t,]+/);
      const keywords = values.map((kw) => kw.trim()).filter(Boolean);

      if (keywords.length > 0) {
        const newKeywords = [...new Set([...localKeywords, ...keywords])];
        setLocalKeywords(newKeywords);
        keywordsConfig?.onChange?.(newKeywords);
        setInputValue("");
      }
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text");
    const keywords = paste
      .split(/[\n\t,]+/)
      .map((kw) => kw.trim())
      .filter(Boolean);
    if (keywords.length > 0) {
      e.preventDefault();
      const newKeywords = [...new Set([...localKeywords, ...keywords])];
      setLocalKeywords(newKeywords);
      keywordsConfig?.onChange?.(newKeywords);
      setInputValue("");
    }
  };

  const removeKeyword = (index) => {
    const newKeywords = localKeywords.filter((_, i) => i !== index);
    setLocalKeywords(newKeywords);
    keywordsConfig?.onChange?.(newKeywords);
  };

  return (
    <div className="edit-product-left1">
      <h2 className="form-title mx-3">
        {keywordsConfig?.title || "Please fill in your information"}
      </h2>

      <Form>
        <div className="mainform edit-product-left">
          {keywordsConfig && (
            <div className="form-section">
              <label className="form-label">
                {keywordsConfig.label || "Keywords"}
              </label>

              <div className="input-container">
                {localKeywords.map((keyword, index) => (
                  <div key={index} className="keyword-badge">
                    {keyword}
                    <button
                      type="button"
                      className="keyword-remove"
                      onClick={() => removeKeyword(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  className="badge-input"
                  placeholder={
                    localKeywords.length === 0
                      ? keywordsConfig.placeholder ||
                        "Paste keywords, or type and press enter/tab/comma to add"
                      : ""
                  }
                />
              </div>
            </div>
          )}

          {textareaConfig && (
            <div className="form-section">
              <label className="form-label">
                {textareaConfig.label || "Additional Details"}
              </label>
              <Form.Control
                as="textarea"
                rows={textareaConfig.rows || 4}
                value={textareaConfig.value || ""}
                onChange={(e) => textareaConfig.onChange(e.target.value)}
                placeholder={textareaConfig.placeholder || "Enter details"}
                className="form-textarea"
              />
            </div>
          )}
          {locationConfig && (
            <div className="form-section">
              <label className="form-label">
                {locationConfig.label || "Location Focus"}
              </label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadLocationOptions}
                value={locationConfig.value}
                onChange={locationConfig.onChange}
                isClearable
                isSearchable
                placeholder={
                  locationConfig.placeholder || "Type to search location..."
                }
                noOptionsMessage={({ inputValue }) =>
                  !inputValue
                    ? "Type to search..."
                    : "No locations found for that query"
                }
                loadingMessage={() => "Searching locations..."}
              />
            </div>
          )}
          {languageConfig && (
            <div className="form-section">
              <label className="form-label">
                {languageConfig.label || "Language"}
              </label>
              <Select
                options={languageOptions}
                value={languageConfig.value}
                onChange={languageConfig.onChange}
                isClearable
                isSearchable
                placeholder={
                  languageConfig.placeholder || "Type to search language..."
                }
              />
            </div>
          )}
        </div>

        {onOptimize && (
          <button
            type="button"
            className="optimize-button mt-2"
            onClick={onOptimize}
            disabled={loading}
          >
            {/* {keywordsConfig?.optimizeButtonText || "Optimize"} */}
            {loading ? (
              <Loader size="small" />
            ) : (
              keywordsConfig?.optimizeButtonText || "Optimize"
            )}
          </button>
        )}

        {onTranslate && (
          <button
            type="button"
            className="optimize-button mt-2"
            onClick={onTranslate}
            disabled={loading}
          >
            {loading ? (
              <Loader size="small" />
            ) : (
              keywordsConfig?.translateButtonText || "Translate"
            )}
          </button>
        )}

        {onAddDocuments && (
          <button
            type="button"
            className="documents-button"
            onClick={onAddDocuments}
          >
            {keywordsConfig?.documentsButtonText || "Add additional documents"}
          </button>
        )}
      </Form>
    </div>
  );
};

export default EditProductLeft;
